/**
 * MultiSelect — React (web) implementation.
 *
 * All styling references the shared variant maps in SingleSelect.ts,
 * which in turn reference the colour/typography elements.
 * No raw hex values or pixel font sizes in this file.
 */

/// <reference lib="dom" />

import React, { useRef, useState, useCallback, useMemo } from 'react';
import {
  normalizeMultiSelectValue,
  selectedMultiSelectSubOption,
  selectMultiSelectSubOption,
  SUB_OPTIONS_INDENT,
  toggleMultiSelectOption,
  type MultiSelectOption,
} from './MultiSelect';
import {
  firstEnabledOptionIndex,
  nextEnabledOptionIndex,
  optionCardColorsFor,
  OPTION_BORDER_WIDTH,
  OPTION_CARD_RADIUS,
  OPTION_CARD_PADDING,
  OPTION_TEXT_GAP,
  LABEL_TO_OPTIONS_GAP,
  OPTION_GAP,
  LABEL_TYPOGRAPHY,
  OPTION_LABEL_TYPOGRAPHY,
  OPTION_DESCRIPTION_TYPOGRAPHY,
  DISABLED_OPACITY,
  isRevealMode,
} from '../SingleSelect/SingleSelect';
import { OptionFeedbackRow } from '../SingleSelect/OptionFeedback.web';
import { optionAccessibilityLabel, revealedBorderStyle } from '../SingleSelect/optionFeedback';
import {
  CHECKBOX_SIZE,
  CHECKBOX_RADIUS,
  CHECKBOX_BORDER_WIDTH,
  CHECKBOX_TICK_ICON,
  CHECKBOX_TICK_SIZE,
  CHECKBOX_TICK_COLOR,
  checkboxBoxColorsFor,
} from '../Checkbox/Checkbox';
import { Icon } from '../Icon/Icon.web';
import { ensureFocusVisibleStyle } from '../../elements/focusStyle.web';
import { mouseHoverHandlers } from '../../elements/mouseHover.web';
import {
  webOptionCardBackground,
  webOptionCardBorder,
  webOptionIndicatorBorder,
  WEB_OPTION_CONTENT_GAP,
} from '../SingleSelect/optionCardStates.web';
import {
  infoDotOptionButtonStyle,
  OptionCardFrame,
  OptionInfoDotContent,
  optionInfoDotReveal,
} from '../SingleSelect/optionInfoDot.web';
import { SingleSelect } from '../SingleSelect/SingleSelect.web';

export interface WebMultiSelectProps {
  /** Analytics id for every option, sub-options included; the generic one when omitted. */
  actionId?: string;
  /** Analytics id for every option's info dot; the generic one when omitted. */
  infoDotActionId?: string;
  label: string;
  hideLabel?: boolean;
  highlightSelectedBorder?: boolean;
  value?: string[];
  options: MultiSelectOption[];
  disabled?: boolean;
  /** Shows every card in its error look and marks each option invalid. Web only. */
  invalid?: boolean;
  onSelect?: (value: string[]) => void;
  'data-testid'?: string;
  'aria-label'?: string;
}

export function MultiSelect({
  actionId = 'ui.multi-select.change',
  infoDotActionId = 'ui.info-dot.toggle',
  label,
  hideLabel = false,
  highlightSelectedBorder = true,
  value = [],
  options,
  disabled = false,
  invalid = false,
  onSelect,
  'data-testid': testId,
  'aria-label': ariaLabel,
}: WebMultiSelectProps) {
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const selectedValues = useMemo(() => normalizeMultiSelectValue(value), [value]);
  // A revealed list shows a committed answer: it stays focusable and readable
  // but no longer takes a new choice.
  const readOnly = isRevealMode(options);

  // Track the roving focus target so tab re-entry lands on the last arrow-navigated option.
  const [rovingIndex, setRovingIndex] = useState(() => {
    const selectedIdx = options.findIndex((o) => !o.disabled && selectedValues.includes(o.value));
    return selectedIdx >= 0 ? selectedIdx : firstEnabledOptionIndex(options);
  });

  // Options can become disabled after mount; the tab stop then falls back to
  // the first selectable option so the group never loses keyboard entry.
  const tabStopIndex =
    options[rovingIndex] && !options[rovingIndex].disabled
      ? rovingIndex
      : firstEnabledOptionIndex(options);

  const handleToggle = useCallback(
    (option: MultiSelectOption) => {
      if (disabled || readOnly || option.disabled) return;
      onSelect?.(toggleMultiSelectOption(selectedValues, option));
    },
    [selectedValues, disabled, readOnly, onSelect],
  );

  const handleSubSelect = useCallback(
    (parent: MultiSelectOption, subValue: string | null) => {
      if (disabled || readOnly || parent.disabled || subValue === null) return;
      onSelect?.(selectMultiSelectSubOption(selectedValues, parent, subValue));
    },
    [selectedValues, disabled, readOnly, onSelect],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const currentIndex = optionRefs.current.findIndex((ref) => ref === document.activeElement);
      if (currentIndex === -1) return;

      let nextIndex: number | undefined;
      if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
        e.preventDefault();
        nextIndex = nextEnabledOptionIndex(options, currentIndex, 1);
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
        e.preventDefault();
        nextIndex = nextEnabledOptionIndex(options, currentIndex, -1);
      }

      if (nextIndex !== undefined) {
        setRovingIndex(nextIndex);
        optionRefs.current[nextIndex]?.focus();
      }
    },
    [options],
  );

  const labelStyle: React.CSSProperties = {
    fontFamily: LABEL_TYPOGRAPHY.fontFamily,
    fontWeight: LABEL_TYPOGRAPHY.fontWeight,
    fontSize: LABEL_TYPOGRAPHY.fontSize,
    lineHeight: `${LABEL_TYPOGRAPHY.lineHeight}px`,
    letterSpacing: LABEL_TYPOGRAPHY.letterSpacing,
    color: optionCardColorsFor(false).labelText,
  };

  const containerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: LABEL_TO_OPTIONS_GAP,
  };

  const optionsStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: OPTION_GAP,
  };

  const subOptionsStyle: React.CSSProperties = {
    paddingLeft: SUB_OPTIONS_INDENT,
  };

  return (
    <div style={containerStyle} data-testid={testId}>
      {!hideLabel && <div style={labelStyle}>{label}</div>}
      <div
        role="group"
        aria-label={ariaLabel ?? label}
        style={optionsStyle}
        onKeyDown={handleKeyDown}
      >
        {options.map((option, index) => {
          const selected = selectedValues.includes(option.value);
          const optionTestId = `${testId ? testId : 'multiselect'}-option-${option.value}`;
          const subOptions = selected ? option.subOptions : undefined;
          return (
            <React.Fragment key={option.value}>
              {/* analytics-exempt: forwarded-wrapper */}
              <OptionCard
                actionId={actionId}
                infoDotActionId={infoDotActionId}
                ref={(el) => {
                  optionRefs.current[index] = el;
                }}
                option={option}
                selected={selected}
                disabled={disabled || option.disabled === true}
                readOnly={readOnly}
                highlightSelectedBorder={highlightSelectedBorder}
                invalid={invalid}
                tabIndex={index === tabStopIndex && !option.disabled ? 0 : -1}
                onPress={() => handleToggle(option)}
                data-testid={optionTestId}
              />
              {subOptions && subOptions.length > 0 ? (
                <div style={subOptionsStyle}>
                  <SingleSelect
                    actionId={actionId}
                    infoDotActionId={infoDotActionId}
                    label={option.subOptionsLabel ?? option.label}
                    hideLabel
                    value={selectedMultiSelectSubOption(selectedValues, option)}
                    options={subOptions.map((sub) => ({
                      ...sub,
                      disabled: disabled || option.disabled || sub.disabled,
                    }))}
                    invalid={invalid}
                    onSelect={(subValue) => handleSubSelect(option, subValue)}
                    data-testid={`${optionTestId}-sub`}
                  />
                </div>
              ) : null}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

interface OptionCardProps {
  actionId: string;
  infoDotActionId: string;
  option: MultiSelectOption;
  selected: boolean;
  disabled: boolean;
  readOnly: boolean;
  highlightSelectedBorder: boolean;
  invalid: boolean;
  tabIndex: 0 | -1;
  onPress: () => void;
  'data-testid'?: string;
}

const FOCUS_STYLE_ID = 'ds-multiselect-focus';

const OptionCard = React.forwardRef<HTMLButtonElement, OptionCardProps>(
  (
    {
      actionId,
      infoDotActionId,
      option,
      selected,
      disabled,
      readOnly,
      highlightSelectedBorder,
      invalid,
      tabIndex,
      onPress,
      'data-testid': testId,
    },
    ref,
  ) => {
    React.useEffect(() => {
      ensureFocusVisibleStyle(FOCUS_STYLE_ID, ['[data-ds-multiselect-option]']);
    }, []);
    const [pointerOver, setPointerOver] = React.useState(false);
    const [infoExpanded, setInfoExpanded] = React.useState(false);
    const descriptionId = React.useId();
    // A revealed (answer-feedback) option is read-only, so it takes no hover affordance.
    const hovered = pointerOver && !disabled && !readOnly;
    const reveal = optionInfoDotReveal(option);
    // When highlightSelectedBorder is false selection is conveyed by the tick
    // alone (neutral divider border in both states, see the native OptionCard);
    // otherwise a selected card paints the primary border. Constant width either
    // way, so selection never reflows the row.
    const cardColors = optionCardColorsFor(selected, highlightSelectedBorder);
    const revealed = revealedBorderStyle(option);

    const webState = { selected, hovered, invalid };

    const cardStyle: React.CSSProperties = {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: WEB_OPTION_CONTENT_GAP,
      padding: OPTION_CARD_PADDING,
      borderRadius: OPTION_CARD_RADIUS,
      backgroundColor: webOptionCardBackground(cardColors.background, webState),
      border: `${OPTION_BORDER_WIDTH}px ${revealed?.borderStyle ?? 'solid'} ${revealed?.borderColor ?? webOptionCardBorder(cardColors.border, webState)}`,
      cursor: disabled || readOnly ? 'default' : 'pointer',
      textAlign: 'left',
      fontFamily: OPTION_LABEL_TYPOGRAPHY.fontFamily,
      width: '100%',
      boxSizing: 'border-box',
      opacity: disabled ? DISABLED_OPACITY : 1,
    };

    const textContentStyle: React.CSSProperties = {
      display: 'flex',
      flexDirection: 'column',
      gap: OPTION_TEXT_GAP,
      flex: 1,
    };

    // Constant regardless of selection — selection is conveyed by the
    // border/tick, never a weight change (Rule: selectors never re-weight
    // their label text).
    const optionLabelStyle: React.CSSProperties = {
      fontFamily: OPTION_LABEL_TYPOGRAPHY.fontFamily,
      fontWeight: OPTION_LABEL_TYPOGRAPHY.fontWeight,
      fontSize: OPTION_LABEL_TYPOGRAPHY.fontSize,
      lineHeight: `${OPTION_LABEL_TYPOGRAPHY.lineHeight}px`,
      letterSpacing: OPTION_LABEL_TYPOGRAPHY.letterSpacing,
      color: cardColors.labelText,
    };

    const descriptionStyle: React.CSSProperties = {
      fontFamily: OPTION_DESCRIPTION_TYPOGRAPHY.fontFamily,
      fontWeight: OPTION_DESCRIPTION_TYPOGRAPHY.fontWeight,
      fontSize: OPTION_DESCRIPTION_TYPOGRAPHY.fontSize,
      lineHeight: `${OPTION_DESCRIPTION_TYPOGRAPHY.lineHeight}px`,
      letterSpacing: OPTION_DESCRIPTION_TYPOGRAPHY.letterSpacing,
      color: cardColors.descriptionText,
    };

    const indicatorColors = checkboxBoxColorsFor(selected);
    const indicatorStyle: React.CSSProperties = {
      width: CHECKBOX_SIZE,
      height: CHECKBOX_SIZE,
      // The border sits inside the size, so the drawn box is the size.
      boxSizing: 'border-box',
      borderRadius: CHECKBOX_RADIUS,
      border: `${CHECKBOX_BORDER_WIDTH}px solid ${webOptionIndicatorBorder(indicatorColors.border, webState)}`,
      backgroundColor: indicatorColors.background,
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    };

    const indicator = (
      <span style={indicatorStyle} aria-hidden="true">
        {selected ? (
          <Icon name={CHECKBOX_TICK_ICON} size={CHECKBOX_TICK_SIZE} color={CHECKBOX_TICK_COLOR} />
        ) : null}
      </span>
    );

    const content = reveal ? (
      <OptionInfoDotContent
        infoDotActionId={infoDotActionId}
        label={option.label}
        reveal={reveal}
        expanded={infoExpanded}
        descriptionId={descriptionId}
        onToggle={() => setInfoExpanded((open) => !open)}
        textContentStyle={textContentStyle}
        labelStyle={optionLabelStyle}
        descriptionStyle={descriptionStyle}
        data-testid={testId}
      />
    ) : null;

    return (
      <OptionCardFrame
        reveal={reveal}
        cardStyle={cardStyle}
        content={content}
        indicator={indicator}
      >
        <button
          data-analytics-action={actionId}
          ref={ref}
          type="button"
          role="checkbox"
          aria-checked={selected}
          aria-disabled={disabled || undefined}
          // ARIA does not allow aria-invalid on role="group", so each checkbox carries it.
          aria-invalid={invalid || undefined}
          aria-label={optionAccessibilityLabel(option)}
          aria-readonly={readOnly || undefined}
          aria-describedby={reveal && infoExpanded ? descriptionId : undefined}
          tabIndex={tabIndex}
          onClick={disabled || readOnly ? undefined : onPress}
          {...mouseHoverHandlers(setPointerOver)}
          style={reveal ? infoDotOptionButtonStyle(disabled) : cardStyle}
          data-testid={testId}
          data-ds-multiselect-option
        >
          {reveal ? null : (
            <>
              <div style={textContentStyle}>
                <span style={optionLabelStyle}>{option.label}</span>
                {option.description && <span style={descriptionStyle}>{option.description}</span>}
                <OptionFeedbackRow
                  option={option}
                  data-testid={testId ? `${testId}-feedback` : undefined}
                />
              </div>
              {indicator}
            </>
          )}
        </button>
      </OptionCardFrame>
    );
  },
);

OptionCard.displayName = 'OptionCard';
